import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Space, Tag, DatePicker, Switch, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface GameType {
  id: number;
  name: string;
  display_name: string;
}

interface CouponInfo {
  id: number;
  code: string;
  discount_type: string;
  discount_value: number;
  max_uses: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  applicable_billing_periods: string[] | null;
  applicable_game_types: number[] | null;
  allow_reuse: boolean;
  max_uses_per_user: number | null;
  status: string;
  created_by_name: string;
  created_at: string;
}

interface UsageRecord {
  id: number;
  user_id: number;
  username: string;
  email: string;
  order_id: number;
  order_type: string;
  order_amount: number | string;
  discount_amount: number | string;
  used_at: string;
  order_created_at: string;
  server_id: number | null;
  server_name: string | null;
  game_type_name: string | null;
}

const CouponManagement: React.FC = () => {
  const [couponData, setCouponData] = useState<CouponInfo[]>([]);
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<CouponInfo | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [usageModalVisible, setUsageModalVisible] = useState(false);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [currentCouponId, setCurrentCouponId] = useState<number | null>(null);
  const [usagePagination, setUsagePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    loadCouponData();
    loadGameTypes();
  }, [pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCouponData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/coupons', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
        },
      });
      setCouponData(response.data.coupons);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      message.error('加载优惠码数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadGameTypes = async () => {
    try {
      const response = await api.get('/games/types');
      setGameTypes(response.data);
    } catch (error) {
      message.error('加载游戏类型失败');
    }
  };

  const handleAdd = () => {
    setEditingCoupon(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: CouponInfo) => {
    setEditingCoupon(record);
    const formData = {
      ...record,
      valid_range: record.valid_from && record.valid_until ? [
        dayjs(record.valid_from),
        dayjs(record.valid_until)
      ] : null,
      allow_reuse: record.allow_reuse !== undefined ? record.allow_reuse : true,
    };
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个优惠码吗？',
      onOk: async () => {
        try {
          await api.delete(`/admin/coupons/${id}`);
          message.success('删除成功');
          loadCouponData();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const submitData = {
        ...values,
        valid_from: values.valid_range ? values.valid_range[0].format('YYYY-MM-DD HH:mm:ss') : null,
        valid_until: values.valid_range ? values.valid_range[1].format('YYYY-MM-DD HH:mm:ss') : null,
      };
      delete submitData.valid_range;

      if (editingCoupon) {
        await api.put(`/admin/coupons/${editingCoupon.id}`, submitData);
        message.success('更新成功');
      } else {
        await api.post('/admin/coupons', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadCouponData();
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const generateCouponCode = async () => {
    try {
      const response = await api.post('/admin/coupons/generate', {
        prefix: 'ARK',
        length: 8,
      });
      form.setFieldsValue({ code: response.data.code });
    } catch (error) {
      message.error('生成优惠码失败');
    }
  };

  const handleViewUsage = async (couponId: number) => {
    setCurrentCouponId(couponId);
    setUsageModalVisible(true);
    await loadUsageRecords(couponId, 1);
  };

  const loadUsageRecords = async (couponId: number, page: number = 1) => {
    setUsageLoading(true);
    try {
      const response = await api.get(`/admin/coupons/${couponId}/usage`, {
        params: {
          page,
          limit: usagePagination.pageSize,
        },
      });
      setUsageRecords(response.data.usageRecords);
      setUsagePagination(prev => ({
        ...prev,
        current: page,
        total: response.data.total,
      }));
    } catch (error) {
      message.error('加载使用记录失败');
    } finally {
      setUsageLoading(false);
    }
  };

  const columns = [
    {
      title: '优惠码',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: '折扣类型',
      dataIndex: 'discount_type',
      key: 'discount_type',
      render: (type: string, record: CouponInfo) => (
        <Tag color={type === 'percentage' ? 'green' : 'orange'}>
          {type === 'percentage' ? `${record.discount_value}%` : `¥${record.discount_value}`}
        </Tag>
      ),
    },
    {
      title: '使用情况',
      key: 'usage',
      render: (_: any, record: CouponInfo) => (
        <div>
          <div>总使用: {record.used_count}/{record.max_uses || '∞'}</div>
          {record.max_uses_per_user && (
            <div className="text-sm text-gray-500">
              每用户限制: {record.max_uses_per_user}次
            </div>
          )}
          {!record.allow_reuse && (
            <div className="text-sm text-orange-500">
              禁止重复使用
            </div>
          )}
        </div>
      ),
    },
    {
      title: '有效期',
      key: 'validity',
      render: (_: any, record: CouponInfo) => {
        if (!record.valid_from && !record.valid_until) {
          return <Tag color="green">永久有效</Tag>;
        }
        const now = dayjs();
        const validFrom = record.valid_from ? dayjs(record.valid_from) : null;
        const validUntil = record.valid_until ? dayjs(record.valid_until) : null;
        
        if (validFrom && now.isBefore(validFrom)) {
          return <Tag color="orange">未生效</Tag>;
        }
        if (validUntil && now.isAfter(validUntil)) {
          return <Tag color="red">已过期</Tag>;
        }
        return <Tag color="green">有效</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: number) => {
        const statusMap: { [key: number]: { color: string; text: string } } = {
          1: { color: 'green', text: '激活' },
          0: { color: 'red', text: '停用' },
        };
        const statusInfo = statusMap[isActive] || { color: 'default', text: '未知' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建者',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CouponInfo) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleViewUsage(record.id)}
            disabled={record.used_count === 0}
          >
            使用记录
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const billingPeriodOptions = [
    { value: 'weekly', label: '周付' },
    { value: 'monthly', label: '月付' },
    { value: 'quarterly', label: '季度' },
    { value: 'semiannual', label: '半年' },
    { value: 'yearly', label: '年付' },
  ];

  return (
    <div className="fade-in">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">优惠码管理</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加优惠码
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={couponData}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize,
              }));
            },
          }}
        />

        <Modal
          title={editingCoupon ? '编辑优惠码' : '添加优惠码'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="code"
                label="优惠码"
                rules={[{ required: true, message: '请输入优惠码' }]}
              >
                <div className="flex space-x-2">
                  <Input placeholder="输入优惠码" size="large" />
                  <Button onClick={generateCouponCode}>
                    生成
                  </Button>
                </div>
              </Form.Item>

              <Form.Item
                name="discount_type"
                label="折扣类型"
                rules={[{ required: true, message: '请选择折扣类型' }]}
              >
                <Select placeholder="选择折扣类型" size="large">
                  <Option value="percentage">百分比折扣</Option>
                  <Option value="fixed">固定金额折扣</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="discount_value"
              label="折扣值"
              rules={[
                { required: true, message: '请输入折扣值' },
                { type: 'number', min: 0, message: '折扣值不能为负数' }
              ]}
            >
              <InputNumber
                placeholder="输入折扣值"
                size="large"
                style={{ width: '100%' }}
                precision={2}
                min={0}
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="max_uses"
                label="最大使用次数"
              >
                <InputNumber
                  placeholder="留空表示无限制"
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                name="max_uses_per_user"
                label="每用户最大使用次数"
              >
                <InputNumber
                  placeholder="留空表示无限制"
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="allow_reuse"
              label="允许重复使用"
              valuePropName="checked"
              tooltip="关闭后，每个用户只能使用一次此优惠码"
            >
              <Switch checkedChildren="允许" unCheckedChildren="禁止" />
            </Form.Item>

            <Form.Item
              name="valid_range"
              label="有效期"
            >
              <RangePicker
                size="large"
                style={{ width: '100%' }}
                showTime
                format="YYYY-MM-DD HH:mm:ss"
              />
            </Form.Item>

            <Form.Item
              name="applicable_billing_periods"
              label="适用的付费周期"
            >
              <Select
                mode="multiple"
                placeholder="选择适用的付费周期"
                size="large"
              >
                {billingPeriodOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="applicable_game_types"
              label="适用的游戏类型"
            >
              <Select
                mode="multiple"
                placeholder="选择适用的游戏类型"
                size="large"
              >
                {gameTypes.map(gameType => (
                  <Option key={gameType.id} value={gameType.id}>
                    {gameType.display_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {editingCoupon && (
              <Form.Item
                name="is_active"
                label="状态"
                valuePropName="checked"
                getValueFromEvent={(checked) => checked ? 1 : 0}
                getValueProps={(value) => ({ checked: value === 1 })}
              >
                <Switch checkedChildren="激活" unCheckedChildren="停用" />
              </Form.Item>
            )}

            <Form.Item className="mb-0">
              <div className="flex justify-end space-x-2">
                <Button onClick={() => setModalVisible(false)}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingCoupon ? '更新' : '创建'}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>

        {/* 使用记录弹窗 */}
        <Modal
          title="优惠码使用记录"
          open={usageModalVisible}
          onCancel={() => setUsageModalVisible(false)}
          footer={null}
          width={800}
        >
          <Table
            columns={[
              {
                title: '用户',
                key: 'user',
                render: (_: any, record: UsageRecord) => (
                  <div>
                    <div className="font-medium">{record.username}</div>
                    <div className="text-sm text-gray-500">{record.email}</div>
                  </div>
                ),
              },
              {
                title: '订单类型',
                dataIndex: 'order_type',
                key: 'order_type',
                render: (type: string, record: UsageRecord) => {
                  const typeMap: { [key: string]: string } = {
                    'server_purchase': '服务器购买',
                    'server_renewal': '服务器续费',
                    'balance_recharge': '余额充值',
                  };
                  
                  const typeText = typeMap[type] || type;
                  
                  // 如果是服务器相关订单，显示详细信息
                  if (type === 'server_purchase' || type === 'server_renewal') {
                    return (
                      <div>
                        <div className="font-medium">{typeText}</div>
                        <div className="text-sm text-gray-500">
                          {record.server_id ? (
                            // 有服务器ID，显示服务器信息
                            <>
                              {record.game_type_name && `${record.game_type_name} - `}
                              {record.server_name || `服务器ID: ${record.server_id}`}
                            </>
                          ) : (
                            // 没有服务器ID，显示订单ID
                            `订单ID: ${record.order_id}`
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return typeText;
                },
              },
              {
                title: '订单金额',
                dataIndex: 'order_amount',
                key: 'order_amount',
                render: (amount: number | string) => `¥${Number(amount).toFixed(2)}`,
              },
              {
                title: '优惠金额',
                dataIndex: 'discount_amount',
                key: 'discount_amount',
                render: (amount: number | string) => (
                  <span className="text-green-600 font-medium">
                    -¥{Number(amount).toFixed(2)}
                  </span>
                ),
              },
              {
                title: '使用时间',
                dataIndex: 'used_at',
                key: 'used_at',
                render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
              },
            ]}
            dataSource={usageRecords}
            loading={usageLoading}
            rowKey="id"
            pagination={{
              current: usagePagination.current,
              pageSize: usagePagination.pageSize,
              total: usagePagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page) => {
                if (currentCouponId) {
                  loadUsageRecords(currentCouponId, page);
                }
              },
            }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default CouponManagement;
